import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export type EmailReportFormat = 'xlsx' | 'csv' | 'pdf';

export interface EmailReportDialogData {
  reportSlug: string;
  reportName: string;
  defaultSubject?: string;
  defaultBody?: string;
  defaultFormat?: EmailReportFormat;
  /** Pre-filled recipient (e.g. user's own email). */
  defaultTo?: string;
}

export interface EmailReportDialogResult {
  to: string[];
  cc: string[];
  subject: string;
  body: string;
  format: EmailReportFormat;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'app-email-report-dialog',
  templateUrl: './email-report-dialog.component.html',
  styleUrls: ['./email-report-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailReportDialogComponent {
  form: FormGroup;
  submitAttempted = false;
  toError = '';
  ccError = '';

  readonly formats: { value: EmailReportFormat; label: string }[] = [
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'csv', label: 'CSV (.csv)' },
    { value: 'pdf', label: 'PDF (.pdf)' }
  ];

  constructor(
    fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: EmailReportDialogData,
    private dialogRef: MatDialogRef<EmailReportDialogComponent, EmailReportDialogResult>
  ) {
    this.form = fb.group({
      to: new FormControl(data.defaultTo ?? '', [Validators.required]),
      cc: new FormControl(''),
      subject: new FormControl(
        data.defaultSubject ?? `${data.reportName} report`,
        [Validators.required]
      ),
      body: new FormControl(
        data.defaultBody ?? `Please find attached the ${data.reportName} report.`
      ),
      format: new FormControl<EmailReportFormat>(data.defaultFormat ?? 'xlsx', [Validators.required])
    });

    // Clear inline error messages as soon as the user types valid input —
    // otherwise the "At least one recipient is required" / "Invalid email"
    // text stayed visible until the next submit attempt (DFCT0014260, 0014314, 0014380).
    this.form.get('to')!.valueChanges.subscribe(v => {
      if (this.toError && this.parseEmails(v).every(e => EMAIL_RE.test(e)) && this.parseEmails(v).length > 0) {
        this.toError = '';
      }
    });
    this.form.get('cc')!.valueChanges.subscribe(v => {
      if (this.ccError && this.parseEmails(v).every(e => EMAIL_RE.test(e))) {
        this.ccError = '';
      }
    });
  }

  cancel(): void { this.dialogRef.close(); }

  send(): void {
    this.submitAttempted = true;
    this.toError = '';
    this.ccError = '';

    const to = this.parseEmails(this.form.value.to);
    const cc = this.parseEmails(this.form.value.cc);

    if (to.length === 0) {
      this.toError = 'At least one recipient is required.';
      return;
    }
    const badTo = to.find(e => !EMAIL_RE.test(e));
    if (badTo) { this.toError = `Invalid email: ${badTo}`; return; }
    const badCc = cc.find(e => !EMAIL_RE.test(e));
    if (badCc) { this.ccError = `Invalid email: ${badCc}`; return; }

    if (this.form.invalid) return;

    this.dialogRef.close({
      to,
      cc,
      subject: (this.form.value.subject ?? '').trim(),
      body: (this.form.value.body ?? '').trim(),
      format: this.form.value.format as EmailReportFormat
    });
  }

  private parseEmails(value: string | null | undefined): string[] {
    if (!value) return [];
    return value
      .split(/[,;\s]+/)
      .map(s => s.trim())
      .filter(s => !!s);
  }
}
